import React, { useState, useEffect, useRef } from 'react';
import ComplianceIndicator from './ComplianceIndicator';
import Icon from '../../../components/AppIcon';
import FieldMergesTable from '../../../components/merge-tags/FieldMergesTable';
import MergeTagPreview from '../../../components/merge-tags/MergeTagPreview';
import TouchpointImageUploader from '../../../components/touchpoint-media/TouchpointImageUploader';
import { formatMonthYear } from '../../../../lib/utils/drip-calculator';
import { describeTouchpointSendWindow } from '../../../../lib/utils/holiday-dates';
import { insertTokenAtCursor } from '../../../utils/merge-tags';

// Extract displayable message - handles legacy JSON-stored format
function getDisplayMessage(raw) {
  if (!raw || typeof raw !== 'string') return raw || '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('{') && trimmed.includes('"message"')) {
    try {
      const parsed = JSON.parse(trimmed);
      return parsed.message || raw;
    } catch {
      return raw;
    }
  }
  return raw;
}

const TouchpointMonthCard = ({ 
  touchpoint, 
  onMessageUpdate, 
  onComplianceCheck,
  onRegenerateMessage,
  onImageUpdate,
  isCurrentMonth, 
  progressInfo 
}) => {
  const [messageText, setMessageText] = useState(getDisplayMessage(touchpoint.messageText) || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [compliance, setCompliance] = useState({
    compliant: true, // Always start as compliant on page load
    issues: [],
    suggestions: []
  });
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [complianceChecking, setComplianceChecking] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [imageUrl, setImageUrl] = useState(touchpoint.imageUrl || null);
  const textareaRef = useRef(null);

  const monthDisplay = formatMonthYear(touchpoint.monthYear);

  useEffect(() => {
    setImageUrl(touchpoint.imageUrl || null);
  }, [touchpoint.imageUrl]);

  useEffect(() => {
    setMessageText(getDisplayMessage(touchpoint.messageText) || '');
  }, [touchpoint.messageText]);

  const sendWindow = describeTouchpointSendWindow(
    touchpoint.sendAfterDate ?? null,
    touchpoint.sendBeforeDate ?? null,
    undefined,
    messageText || touchpoint.messageText,
    touchpoint.isHolidayMessage ?? null
  );

  const sendWindowBadgeClass = {
    waiting: 'bg-amber-100 text-amber-800 border-amber-200',
    active: 'bg-primary/10 text-primary border-primary/30',
    open: 'bg-green-100 text-green-800 border-green-200',
    past: 'bg-muted text-muted-foreground border-border',
    spread: 'bg-slate-100 text-slate-700 border-slate-200',
  }[sendWindow?.status] || 'bg-muted text-muted-foreground border-border';

  // Handle initial load - show loading then transition to compliant (never show issues on load)
  useEffect(() => {
    if (isInitialLoad && messageText) {
      // Show loading for a brief moment, then set to compliant
      setTimeout(() => {
        setCompliance({
          compliant: true, // Always start as compliant on page load
          issues: [],
          suggestions: []
        });
        setIsInitialLoad(false);
      }, 1000); // 1 second loading state
    }
  }, [isInitialLoad, messageText]);

  const handleSave = async () => {
    if (!touchpoint.id) return;

    setIsSaving(true);
    try {
      const result = await onMessageUpdate(touchpoint.id, messageText);
      if (result.success) {
        setIsEditing(false);
        setCompliance({
          compliant: true,
          issues: [],
          suggestions: []
        });
      } else {
        alert(`Failed to save: ${result.error}`);
      }
    } catch (error) {
      alert(`Error saving: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplianceCheck = async () => {
    if (!messageText.trim()) return;

    setComplianceChecking(true);
    try {
      const result = await onComplianceCheck(messageText);
      setCompliance(result);
    } catch (error) {
      console.error('Compliance check error:', error);
    } finally {
      setComplianceChecking(false);
    }
  };

  const handleChangeMessage = async () => {
    console.log('=== CHANGE MESSAGE BUTTON CLICKED ===');
    console.log('Full touchpoint object:', touchpoint);
    console.log('Touchpoint ID:', touchpoint.id);
    console.log('Month Year:', touchpoint.monthYear);
    
    if (!touchpoint.id) {
      console.log('ERROR: No touchpoint ID');
      console.log('Available touchpoint properties:', Object.keys(touchpoint));
      return;
    }

    setIsRegenerating(true);
    try {
      console.log('Calling onRegenerateMessage with:', { touchpointId: touchpoint.id, monthYear: touchpoint.monthYear });
      const result = await onRegenerateMessage(touchpoint.id, touchpoint.monthYear);
      console.log('Change message result:', result);
      
      if (result.success) {
        console.log('Success! New message:', result.data.messageText);
        setMessageText(result.data.messageText);
        setCompliance({
          compliant: result.data.complianceStatus === 'compliant',
          issues: result.data.complianceIssues || [],
          suggestions: result.data.complianceSuggestions || []
        });
        console.log('State updated successfully');
      } else {
        console.log('API returned error:', result.error);
        alert(`Failed to change message: ${result.error}`);
      }
    } catch (error) {
      console.error('Error changing message:', error);
      alert(`Error changing message: ${error.message}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleTextChange = (e) => {
    setMessageText(e.target.value);
    // Auto-check compliance after a short delay
    clearTimeout(window.complianceTimeout);
    window.complianceTimeout = setTimeout(() => {
      if (e.target.value.trim()) {
        handleComplianceCheck();
      }
    }, 1000);
  };

  const handleInsertMerge = (token) => {
    const { next, selectionStart } = insertTokenAtCursor(textareaRef.current, messageText, token);
    setMessageText(next);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(selectionStart, selectionStart);
      }
    });
    clearTimeout(window.complianceTimeout);
    window.complianceTimeout = setTimeout(() => {
      if (next.trim()) handleComplianceCheck();
    }, 1000);
  };

  const handleImageSaved = async (url) => {
    setImageUrl(url);
    if (onImageUpdate) {
      await onImageUpdate(touchpoint.id, url);
    }
  };

  const renderProgressBar = () => {
    if (!isCurrentMonth || !progressInfo) return null;

    const { totalClients, eligibleClients, dailyQuota, daysToComplete, businessDaysRemaining } = progressInfo;
    // eligibleClients = clients still to receive this month; the bar fills as that count drops.
    const sentClients = Math.max(0, totalClients - eligibleClients);
    const progressPercentage = totalClients > 0
      ? Math.min(100, (sentClients / totalClients) * 100)
      : 0;
    const daysRemaining = daysToComplete ?? (
      dailyQuota > 0 && eligibleClients > 0
        ? Math.ceil(eligibleClients / dailyQuota)
        : 0
    );

    // A holiday window that hasn't opened yet must not claim clients already received it.
    const holidayWaiting = sendWindow?.status === 'waiting';
    const holidayClosed = sendWindow?.status === 'past';
    const clientCount = (n) => `${n} client${n === 1 ? '' : 's'}`;

    let quotaLine = '';
    if (totalClients === 0) {
      quotaLine = 'Add clients with phone numbers to start sending';
    } else if (holidayWaiting) {
      quotaLine = eligibleClients > 0
        ? `Sending hasn't started — ${clientCount(eligibleClients)} queued for the holiday window`
        : 'Queued for the holiday window';
    } else if (eligibleClients === 0) {
      quotaLine = holidayClosed
        ? 'Holiday window closed — everyone got this month\'s message'
        : 'All clients received this month\'s message';
    } else if (dailyQuota > 0) {
      quotaLine = `${dailyQuota} messages/day • ~${daysRemaining} business day${daysRemaining === 1 ? '' : 's'} to finish`;
      if (businessDaysRemaining != null) {
        quotaLine += ` (${businessDaysRemaining} send days left this month)`;
      }
    } else {
      quotaLine = 'Calculating send schedule…';
    }

    return (
      <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex justify-between text-sm text-primary mb-2">
          <span className="font-medium">Sending Progress</span>
          <span>{sentClients}/{totalClients} clients</span>
        </div>
        <div className="w-full bg-primary/20 rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <div className="mt-2 text-xs text-primary/80">
          {quotaLine}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-card border border-border rounded-lg elevation-2 p-6 transition-smooth ${
      isCurrentMonth ? 'ring-2 ring-primary border-primary/50' : 'hover:elevation-3'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {monthDisplay}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
            {isCurrentMonth && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                Currently Sending
              </span>
            )}
            {sendWindow && (
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${sendWindowBadgeClass}`}
                title="When holiday-specific copy sends; after the end date the sender uses a timeless fallback"
              >
                <Icon name={sendWindow.status === 'waiting' ? 'Clock' : 'CalendarCheck'} size={10} />
                  {sendWindow.label}
                </span>
              )}
            </div>
            {sendWindow?.detail && (
              <p className="text-xs text-muted-foreground mt-1 max-w-md">{sendWindow.detail}</p>
            )}
          </div>
          {touchpoint.personalityTypeUsed && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
              {touchpoint.personalityTypeUsed === 'professional' && '💼 Professional'}
              {touchpoint.personalityTypeUsed === 'warm_personal' && '❤️ Warm'}
              {touchpoint.personalityTypeUsed === 'question_based' && '💬 Question'}
              {touchpoint.personalityTypeUsed === 'observational' && '👁️ Observational'}
            </span>
          )}
        </div>
        <ComplianceIndicator
          compliance={compliance}
          checking={complianceChecking || isInitialLoad}
        />
      </div>

      {/* Message Content */}
      <div className="mb-4">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={handleTextChange}
              className="w-full h-24 p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-ring focus:border-transparent bg-input text-foreground placeholder-muted-foreground"
              placeholder="Enter your touchpoint message..."
              maxLength={500}
            />
            <FieldMergesTable onInsert={handleInsertMerge} />
            <div className="p-3 bg-muted/40 rounded-lg border border-border">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                Preview
              </p>
              <MergeTagPreview text={messageText} />
            </div>
            <div className="mt-2 flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {messageText.length}/500 characters
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setMessageText(getDisplayMessage(touchpoint.messageText) || '');
                  }}
                  className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !compliance.compliant}
                  className={`px-4 py-1 text-sm rounded-lg transition-smooth touch-target ${
                    isSaving || !compliance.compliant
                      ? 'bg-muted text-muted-foreground cursor-not-allowed'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="p-3 bg-muted/50 rounded-lg min-h-[6rem]">
              {messageText ? (
                <MergeTagPreview text={messageText} />
              ) : (
                <p className="text-foreground whitespace-pre-wrap">No message generated yet...</p>
              )}
            </div>
            <div className="mt-3 flex gap-3">
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm text-primary hover:text-primary/80 font-medium transition-smooth"
              >
                Edit Message
              </button>
              <button
                onClick={handleChangeMessage}
                disabled={isRegenerating}
                className={`text-sm font-medium transition-smooth flex items-center gap-1 ${
                  isRegenerating 
                    ? 'text-muted-foreground cursor-not-allowed' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {isRegenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-muted-foreground"></div>
                    Changing...
                  </>
                ) : (
                  <>
                    <Icon name="RefreshCw" size={12} />
                    Change Message
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {touchpoint.id && (
        <div className="mb-4">
          <TouchpointImageUploader
            imageUrl={imageUrl}
            purpose="touchpoint"
            touchpointId={touchpoint.id}
            onSaved={handleImageSaved}
            alignHint="this month's message"
          />
        </div>
      )}

      {/* Progress Bar for Current Month */}
      {renderProgressBar()}

      {/* Compliance Issues */}
      {!compliance.compliant && compliance.issues.length > 0 && (
        <div className="mt-4 p-3 bg-error/5 border border-error/20 rounded-lg">
          <h4 className="text-sm font-medium text-error mb-2">Compliance Issues:</h4>
          <ul className="text-sm text-error/80 space-y-1">
            {compliance.issues.map((issue, index) => (
              <li key={index}>• {issue}</li>
            ))}
          </ul>
          {compliance.suggestions.length > 0 && (
            <div className="mt-2">
              <h5 className="text-sm font-medium text-error">Suggestions:</h5>
              <ul className="text-sm text-error/70 space-y-1">
                {compliance.suggestions.map((suggestion, index) => (
                  <li key={index}>• {suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TouchpointMonthCard;
